import type { SchoolLevel } from '@jeraisonne/db';
import type { DemoContent } from './types';

/**
 * Données de démo locales — utilisées quand aucune `demo_config` active
 * n'existe en base, ou que la RPC échoue. Garantissent que `/demo`
 * fonctionne sans dépendance à la DB.
 *
 * Volontairement simples : 5 QCM warmup + 1 exercice ≤ 5 étapes par niveau.
 */

function content(level: SchoolLevel, data: Omit<DemoContent, 'level' | 'source'>): DemoContent {
  return { level, source: 'fallback', ...data };
}

const SIXIEME: DemoContent = content('6e', {
  warmup: [
    {
      id: '6e-w1',
      prompt_md: 'Combien fait $7 \\times 8$ ?',
      explanation_md: '$7 \\times 8 = 56$. Table de 7 ou table de 8 — à connaître par cœur.',
      choices: [
        { id: 'a', label_md: '$54$', is_correct: false, feedback_md: 'C’est $9 \\times 6$.' },
        { id: 'b', label_md: '$56$', is_correct: true },
        { id: 'c', label_md: '$58$', is_correct: false },
        { id: 'd', label_md: '$64$', is_correct: false, feedback_md: 'C’est $8 \\times 8$.' },
      ],
    },
    {
      id: '6e-w2',
      prompt_md: 'Quelle fraction est égale à $\\frac{1}{2}$ ?',
      explanation_md: '$\\frac{1}{2} = \\frac{4}{8}$ car $4 \\times 2 = 8$.',
      choices: [
        { id: 'a', label_md: '$\\frac{2}{3}$', is_correct: false },
        { id: 'b', label_md: '$\\frac{4}{8}$', is_correct: true },
        { id: 'c', label_md: '$\\frac{3}{5}$', is_correct: false },
        { id: 'd', label_md: '$\\frac{2}{5}$', is_correct: false },
      ],
    },
    {
      id: '6e-w3',
      prompt_md: 'Quel est le périmètre d’un carré de côté $5$ cm ?',
      explanation_md: 'Périmètre d’un carré = $4 \\times \\text{côté} = 4 \\times 5 = 20$ cm.',
      choices: [
        { id: 'a', label_md: '$10$ cm', is_correct: false, feedback_md: 'C’est $2 \\times 5$.' },
        { id: 'b', label_md: '$15$ cm', is_correct: false },
        { id: 'c', label_md: '$20$ cm', is_correct: true },
        {
          id: 'd',
          label_md: '$25$ cm',
          is_correct: false,
          feedback_md: 'C’est l’aire ($5 \\times 5$), pas le périmètre.',
        },
      ],
    },
    {
      id: '6e-w4',
      prompt_md: 'Convertis $1{,}5$ m en cm.',
      explanation_md: '$1$ m = $100$ cm, donc $1{,}5$ m = $150$ cm.',
      choices: [
        { id: 'a', label_md: '$15$ cm', is_correct: false },
        { id: 'b', label_md: '$150$ cm', is_correct: true },
        { id: 'c', label_md: '$1{,}5$ cm', is_correct: false },
        { id: 'd', label_md: '$1500$ cm', is_correct: false },
      ],
    },
    {
      id: '6e-w5',
      prompt_md: 'Combien fait $48 : 6$ ?',
      explanation_md: '$48 : 6 = 8$ car $6 \\times 8 = 48$.',
      choices: [
        { id: 'a', label_md: '$6$', is_correct: false },
        { id: 'b', label_md: '$7$', is_correct: false },
        { id: 'c', label_md: '$8$', is_correct: true },
        { id: 'd', label_md: '$9$', is_correct: false },
      ],
    },
  ],
  exercise: {
    title: 'Calculer le périmètre et l’aire d’un rectangle',
    chapter_label: 'Grandeurs et mesures',
    steps: [
      {
        id: '6e-ex-s1',
        prompt_md:
          'Un rectangle mesure $7$ cm de longueur et $4$ cm de largeur. Quelle est la formule du périmètre ?',
        explanation_md: 'Périmètre = $2 \\times (\\text{longueur} + \\text{largeur})$.',
        choices: [
          {
            id: 'a',
            label_md: '$\\text{longueur} \\times \\text{largeur}$',
            is_correct: false,
            feedback_md: 'Ça, c’est l’aire.',
          },
          {
            id: 'b',
            label_md: '$2 \\times (\\text{longueur} + \\text{largeur})$',
            is_correct: true,
          },
          { id: 'c', label_md: '$\\text{longueur} + \\text{largeur}$', is_correct: false },
          { id: 'd', label_md: '$4 \\times \\text{côté}$', is_correct: false },
        ],
      },
      {
        id: '6e-ex-s2',
        prompt_md: 'Quel calcul correspond au périmètre du rectangle de $7$ cm sur $4$ cm ?',
        explanation_md: '$2 \\times (7 + 4) = 2 \\times 11 = 22$ cm.',
        choices: [
          { id: 'a', label_md: '$2 \\times 7 + 4$', is_correct: false },
          { id: 'b', label_md: '$2 \\times (7 + 4)$', is_correct: true },
          {
            id: 'c',
            label_md: '$7 \\times 4$',
            is_correct: false,
            feedback_md: 'Ça, c’est l’aire.',
          },
          { id: 'd', label_md: '$7 + 4$', is_correct: false },
        ],
      },
      {
        id: '6e-ex-s3',
        prompt_md: 'Combien vaut $2 \\times (7 + 4)$ ?',
        explanation_md: '$7 + 4 = 11$, puis $2 \\times 11 = 22$.',
        choices: [
          { id: 'a', label_md: '$18$', is_correct: false },
          { id: 'b', label_md: '$20$', is_correct: false },
          { id: 'c', label_md: '$22$', is_correct: true },
          { id: 'd', label_md: '$28$', is_correct: false, feedback_md: 'C’est $7 \\times 4$.' },
        ],
      },
      {
        id: '6e-ex-s4',
        prompt_md: 'Quelle est l’aire du rectangle de $7$ cm sur $4$ cm ?',
        explanation_md: 'Aire = $\\text{longueur} \\times \\text{largeur} = 7 \\times 4 = 28$ cm².',
        choices: [
          { id: 'a', label_md: '$11$ cm²', is_correct: false },
          { id: 'b', label_md: '$22$ cm²', is_correct: false, feedback_md: 'C’est le périmètre.' },
          { id: 'c', label_md: '$28$ cm²', is_correct: true },
          { id: 'd', label_md: '$14$ cm²', is_correct: false },
        ],
      },
    ],
  },
});

const CINQUIEME: DemoContent = content('5e', {
  warmup: [
    {
      id: '5e-w1',
      prompt_md: 'Que vaut $-3 + 7$ ?',
      explanation_md:
        'Sur la droite graduée, on part de $-3$ et on avance de $7$ vers la droite → $4$.',
      choices: [
        { id: 'a', label_md: '$-10$', is_correct: false },
        { id: 'b', label_md: '$-4$', is_correct: false, feedback_md: 'Tu as soustrait.' },
        { id: 'c', label_md: '$4$', is_correct: true },
        { id: 'd', label_md: '$10$', is_correct: false },
      ],
    },
    {
      id: '5e-w2',
      prompt_md: 'Que vaut $\\frac{2}{3} + \\frac{1}{3}$ ?',
      explanation_md: 'Même dénominateur : on additionne les numérateurs : $\\frac{2+1}{3} = 1$.',
      choices: [
        { id: 'a', label_md: '$\\frac{2}{6}$', is_correct: false },
        { id: 'b', label_md: '$\\frac{3}{6}$', is_correct: false },
        { id: 'c', label_md: '$1$', is_correct: true },
        { id: 'd', label_md: '$\\frac{3}{3}^2$', is_correct: false },
      ],
    },
    {
      id: '5e-w3',
      prompt_md: 'Quel est le résultat de $5 + 3 \\times 2$ ?',
      explanation_md: 'Priorités : on fait $3 \\times 2 = 6$ d’abord, puis $5 + 6 = 11$.',
      choices: [
        {
          id: 'a',
          label_md: '$16$',
          is_correct: false,
          feedback_md: 'Tu as calculé de gauche à droite, sans priorité.',
        },
        { id: 'b', label_md: '$11$', is_correct: true },
        { id: 'c', label_md: '$13$', is_correct: false },
        { id: 'd', label_md: '$10$', is_correct: false },
      ],
    },
    {
      id: '5e-w4',
      prompt_md: 'La somme des angles d’un triangle vaut :',
      explanation_md: 'Pour tout triangle, la somme des angles vaut toujours $180°$.',
      choices: [
        { id: 'a', label_md: '$90°$', is_correct: false },
        { id: 'b', label_md: '$180°$', is_correct: true },
        {
          id: 'c',
          label_md: '$360°$',
          is_correct: false,
          feedback_md: 'C’est la somme pour un quadrilatère.',
        },
        { id: 'd', label_md: '$120°$', is_correct: false },
      ],
    },
    {
      id: '5e-w5',
      prompt_md: '$25 \\%$ de $80$ vaut :',
      explanation_md: '$25\\%$ = $\\frac{1}{4}$. $\\frac{1}{4}$ de $80$ = $80 : 4 = 20$.',
      choices: [
        { id: 'a', label_md: '$10$', is_correct: false },
        { id: 'b', label_md: '$15$', is_correct: false },
        { id: 'c', label_md: '$20$', is_correct: true },
        { id: 'd', label_md: '$25$', is_correct: false },
      ],
    },
  ],
  exercise: {
    title: 'Calcul avec priorités',
    chapter_label: 'Nombres et calculs',
    steps: [
      {
        id: '5e-ex-s1',
        prompt_md:
          'On veut calculer $A = 12 - 2 \\times 3 + 5$. Quel opérateur doit-on traiter en premier ?',
        explanation_md: 'La multiplication est prioritaire sur l’addition et la soustraction.',
        choices: [
          { id: 'a', label_md: 'La soustraction $-$', is_correct: false },
          { id: 'b', label_md: 'La multiplication $\\times$', is_correct: true },
          { id: 'c', label_md: 'L’addition $+$', is_correct: false },
          {
            id: 'd',
            label_md: 'On lit de gauche à droite',
            is_correct: false,
            feedback_md: 'Seulement si les opérations ont le même rang de priorité.',
          },
        ],
      },
      {
        id: '5e-ex-s2',
        prompt_md: 'Que vaut $2 \\times 3$ ?',
        explanation_md: '$2 \\times 3 = 6$.',
        choices: [
          { id: 'a', label_md: '$5$', is_correct: false },
          { id: 'b', label_md: '$6$', is_correct: true },
          { id: 'c', label_md: '$8$', is_correct: false },
          { id: 'd', label_md: '$23$', is_correct: false },
        ],
      },
      {
        id: '5e-ex-s3',
        prompt_md: 'On remplace : $A = 12 - 6 + 5$. Quelle est l’étape suivante ?',
        explanation_md:
          'Soustraction et addition ont la même priorité : on lit de gauche à droite, donc on commence par $12 - 6$.',
        choices: [
          {
            id: 'a',
            label_md: 'Calculer $6 + 5$ d’abord',
            is_correct: false,
            feedback_md:
              'Non — quand les opérations ont la même priorité, on lit de gauche à droite.',
          },
          { id: 'b', label_md: 'Calculer $12 - 6$', is_correct: true },
          { id: 'c', label_md: 'Mettre des parenthèses autour de $6 + 5$', is_correct: false },
          { id: 'd', label_md: 'Reprendre la multiplication', is_correct: false },
        ],
      },
      {
        id: '5e-ex-s4',
        prompt_md: 'Combien vaut $A$ ?',
        explanation_md: '$12 - 6 = 6$, puis $6 + 5 = 11$.',
        choices: [
          { id: 'a', label_md: '$11$', is_correct: true },
          { id: 'b', label_md: '$1$', is_correct: false, feedback_md: 'C’est $12 - (6 + 5)$.' },
          { id: 'c', label_md: '$17$', is_correct: false },
          { id: 'd', label_md: '$13$', is_correct: false },
        ],
      },
    ],
  },
});

const QUATRIEME: DemoContent = content('4e', {
  warmup: [
    {
      id: '4e-w1',
      prompt_md: 'Que vaut $(-3)^2$ ?',
      explanation_md: '$(-3)^2 = (-3) \\times (-3) = 9$. Le carré d’un nombre négatif est positif.',
      choices: [
        {
          id: 'a',
          label_md: '$-9$',
          is_correct: false,
          feedback_md: 'Attention au signe : produit de deux nombres négatifs.',
        },
        { id: 'b', label_md: '$-6$', is_correct: false },
        { id: 'c', label_md: '$6$', is_correct: false },
        { id: 'd', label_md: '$9$', is_correct: true },
      ],
    },
    {
      id: '4e-w2',
      prompt_md: '$\\frac{3}{4} \\times \\frac{2}{5} = ?$',
      explanation_md:
        'Produit de fractions : $\\frac{3 \\times 2}{4 \\times 5} = \\frac{6}{20} = \\frac{3}{10}$.',
      choices: [
        { id: 'a', label_md: '$\\frac{5}{9}$', is_correct: false },
        { id: 'b', label_md: '$\\frac{6}{20}$', is_correct: true },
        { id: 'c', label_md: '$\\frac{3}{20}$', is_correct: false },
        {
          id: 'd',
          label_md: '$\\frac{15}{8}$',
          is_correct: false,
          feedback_md: 'C’est la division, pas le produit.',
        },
      ],
    },
    {
      id: '4e-w3',
      prompt_md: 'Développe : $3(x + 4) = ?$',
      explanation_md: 'Distributivité : $3 \\times x + 3 \\times 4 = 3x + 12$.',
      choices: [
        {
          id: 'a',
          label_md: '$3x + 4$',
          is_correct: false,
          feedback_md: 'Tu as oublié de distribuer $3$ sur le $4$.',
        },
        { id: 'b', label_md: '$3x + 12$', is_correct: true },
        { id: 'c', label_md: '$x + 12$', is_correct: false },
        { id: 'd', label_md: '$3x \\times 12$', is_correct: false },
      ],
    },
    {
      id: '4e-w4',
      prompt_md: 'Dans un triangle rectangle, le théorème qui relie les trois côtés est :',
      explanation_md: 'Théorème de Pythagore : $a^2 + b^2 = c^2$ où $c$ est l’hypoténuse.',
      choices: [
        { id: 'a', label_md: 'Théorème de Thalès', is_correct: false },
        { id: 'b', label_md: 'Théorème de Pythagore', is_correct: true },
        {
          id: 'c',
          label_md: 'Théorème d’Al-Kashi',
          is_correct: false,
          feedback_md: 'C’est la généralisation, mais ici un cas particulier suffit.',
        },
        { id: 'd', label_md: 'Aucun', is_correct: false },
      ],
    },
    {
      id: '4e-w5',
      prompt_md: 'Convertis $2 \\times 10^3$ en écriture décimale.',
      explanation_md: '$10^3 = 1000$, donc $2 \\times 10^3 = 2000$.',
      choices: [
        { id: 'a', label_md: '$200$', is_correct: false },
        { id: 'b', label_md: '$2000$', is_correct: true },
        { id: 'c', label_md: '$20\\,000$', is_correct: false },
        { id: 'd', label_md: '$0{,}002$', is_correct: false },
      ],
    },
  ],
  exercise: {
    title: 'Pythagore — calcul d’une longueur',
    chapter_label: 'Géométrie',
    steps: [
      {
        id: '4e-ex-s1',
        prompt_md:
          'Un triangle $ABC$ est rectangle en $A$. On a $AB = 3$ cm et $AC = 4$ cm. Quel théorème permet de calculer $BC$ ?',
        explanation_md:
          'Le triangle est rectangle en $A$, donc $BC$ est l’hypoténuse. Théorème de Pythagore.',
        choices: [
          { id: 'a', label_md: 'Théorème de Thalès', is_correct: false },
          { id: 'b', label_md: 'Théorème de Pythagore', is_correct: true },
          { id: 'c', label_md: 'Calcul du périmètre', is_correct: false },
          {
            id: 'd',
            label_md: 'Trigonométrie',
            is_correct: false,
            feedback_md: 'C’est pour les angles, pas vu en 4e.',
          },
        ],
      },
      {
        id: '4e-ex-s2',
        prompt_md: 'Quelle égalité écrit Pythagore dans ce triangle ?',
        explanation_md:
          'Le carré de l’hypoténuse $BC$ est égal à la somme des carrés des deux autres côtés.',
        choices: [
          { id: 'a', label_md: '$BC^2 = AB^2 + AC^2$', is_correct: true },
          {
            id: 'b',
            label_md: '$AB^2 = BC^2 + AC^2$',
            is_correct: false,
            feedback_md: '$AB$ n’est pas l’hypoténuse.',
          },
          { id: 'c', label_md: '$BC = AB + AC$', is_correct: false },
          { id: 'd', label_md: '$BC^2 = AB \\times AC$', is_correct: false },
        ],
      },
      {
        id: '4e-ex-s3',
        prompt_md: 'On remplace : $BC^2 = 3^2 + 4^2$. Combien vaut $BC^2$ ?',
        explanation_md: '$3^2 + 4^2 = 9 + 16 = 25$.',
        choices: [
          { id: 'a', label_md: '$7$', is_correct: false, feedback_md: 'Tu as additionné $3 + 4$.' },
          { id: 'b', label_md: '$12$', is_correct: false, feedback_md: 'C’est $3 \\times 4$.' },
          { id: 'c', label_md: '$25$', is_correct: true },
          { id: 'd', label_md: '$49$', is_correct: false },
        ],
      },
      {
        id: '4e-ex-s4',
        prompt_md: 'Combien vaut $BC$ ?',
        explanation_md: '$BC = \\sqrt{25} = 5$ cm.',
        choices: [
          { id: 'a', label_md: '$5$ cm', is_correct: true },
          {
            id: 'b',
            label_md: '$25$ cm',
            is_correct: false,
            feedback_md: 'C’est $BC^2$, pas $BC$.',
          },
          { id: 'c', label_md: '$12{,}5$ cm', is_correct: false },
          { id: 'd', label_md: '$2{,}5$ cm', is_correct: false },
        ],
      },
    ],
  },
});

const TROISIEME: DemoContent = content('3e', {
  warmup: [
    {
      id: '3e-w1',
      prompt_md: 'Factorise : $x^2 - 9$.',
      explanation_md: 'Identité remarquable $a^2 - b^2 = (a - b)(a + b)$, avec $a = x$ et $b = 3$.',
      choices: [
        {
          id: 'a',
          label_md: '$(x - 9)(x + 9)$',
          is_correct: false,
          feedback_md: 'Attention : ici $b^2 = 9$, donc $b = 3$.',
        },
        { id: 'b', label_md: '$(x - 3)(x + 3)$', is_correct: true },
        { id: 'c', label_md: '$(x - 3)^2$', is_correct: false },
        { id: 'd', label_md: '$x(x - 9)$', is_correct: false },
      ],
    },
    {
      id: '3e-w2',
      prompt_md: 'Développe : $(x + 2)^2$.',
      explanation_md: '$(a + b)^2 = a^2 + 2ab + b^2$, donc $x^2 + 4x + 4$.',
      choices: [
        {
          id: 'a',
          label_md: '$x^2 + 4$',
          is_correct: false,
          feedback_md: 'Le double produit est manquant.',
        },
        { id: 'b', label_md: '$x^2 + 2x + 4$', is_correct: false },
        { id: 'c', label_md: '$x^2 + 4x + 4$', is_correct: true },
        { id: 'd', label_md: '$x^2 + 4x + 2$', is_correct: false },
      ],
    },
    {
      id: '3e-w3',
      prompt_md: 'Que vaut $\\sqrt{50}$ sous forme simplifiée ?',
      explanation_md: '$\\sqrt{50} = \\sqrt{25 \\times 2} = 5\\sqrt{2}$.',
      choices: [
        { id: 'a', label_md: '$5\\sqrt{2}$', is_correct: true },
        { id: 'b', label_md: '$10\\sqrt{5}$', is_correct: false },
        {
          id: 'c',
          label_md: '$2\\sqrt{25}$',
          is_correct: false,
          feedback_md: 'Pas simplifié : $\\sqrt{25} = 5$.',
        },
        { id: 'd', label_md: '$25\\sqrt{2}$', is_correct: false },
      ],
    },
    {
      id: '3e-w4',
      prompt_md: 'Une fonction linéaire $f(x) = 3x$ vérifie :',
      explanation_md: 'Une fonction linéaire passe par l’origine : $f(0) = 0$.',
      choices: [
        { id: 'a', label_md: '$f(0) = 3$', is_correct: false, feedback_md: 'C’est $f(1)$.' },
        { id: 'b', label_md: '$f(0) = 0$', is_correct: true },
        { id: 'c', label_md: '$f(0) = -3$', is_correct: false },
        { id: 'd', label_md: '$f(0)$ n’existe pas', is_correct: false },
      ],
    },
    {
      id: '3e-w5',
      prompt_md: 'Théorème de Thalès s’applique quand :',
      explanation_md:
        'Deux droites sécantes coupées par deux droites parallèles : on peut alors écrire l’égalité des rapports.',
      choices: [
        {
          id: 'a',
          label_md: 'Le triangle est rectangle',
          is_correct: false,
          feedback_md: 'C’est Pythagore.',
        },
        { id: 'b', label_md: 'Deux droites parallèles coupent deux sécantes', is_correct: true },
        { id: 'c', label_md: 'Les côtés sont égaux', is_correct: false },
        { id: 'd', label_md: 'Le triangle est isocèle', is_correct: false },
      ],
    },
  ],
  exercise: {
    title: 'Résoudre une équation produit',
    chapter_label: 'Calcul littéral · Brevet',
    steps: [
      {
        id: '3e-ex-s1',
        prompt_md: 'On veut résoudre $(x - 2)(x + 5) = 0$. Que dit la règle du produit nul ?',
        explanation_md:
          'Un produit est nul si et seulement si l’un (au moins) de ses facteurs est nul.',
        choices: [
          {
            id: 'a',
            label_md: 'Le produit est nul si les deux facteurs sont nuls',
            is_correct: false,
            feedback_md: 'Il suffit qu’un seul soit nul.',
          },
          {
            id: 'b',
            label_md: 'Le produit est nul si au moins un facteur est nul',
            is_correct: true,
          },
          {
            id: 'c',
            label_md: 'Il faut développer puis résoudre',
            is_correct: false,
            feedback_md: 'Pas nécessaire — la forme factorisée fait gagner du temps.',
          },
          { id: 'd', label_md: 'L’équation n’a pas de solution', is_correct: false },
        ],
      },
      {
        id: '3e-ex-s2',
        prompt_md: 'Quelle équation résout-on pour le premier facteur ?',
        explanation_md: 'On annule chaque facteur séparément. Premier facteur : $x - 2 = 0$.',
        choices: [
          { id: 'a', label_md: '$x - 2 = 0$', is_correct: true },
          { id: 'b', label_md: '$x + 2 = 0$', is_correct: false },
          { id: 'c', label_md: '$x \\times 2 = 0$', is_correct: false },
          {
            id: 'd',
            label_md: '$x = 5$',
            is_correct: false,
            feedback_md: 'C’est le deuxième facteur.',
          },
        ],
      },
      {
        id: '3e-ex-s3',
        prompt_md: 'Quelle est la solution de $x - 2 = 0$ ?',
        explanation_md: '$x - 2 = 0 \\iff x = 2$.',
        choices: [
          { id: 'a', label_md: '$x = -2$', is_correct: false },
          { id: 'b', label_md: '$x = 0$', is_correct: false },
          { id: 'c', label_md: '$x = 2$', is_correct: true },
          { id: 'd', label_md: '$x = 1$', is_correct: false },
        ],
      },
      {
        id: '3e-ex-s4',
        prompt_md: 'Et pour le deuxième facteur, $x + 5 = 0$ ?',
        explanation_md: '$x + 5 = 0 \\iff x = -5$.',
        choices: [
          { id: 'a', label_md: '$x = 5$', is_correct: false, feedback_md: 'Attention au signe.' },
          { id: 'b', label_md: '$x = -5$', is_correct: true },
          { id: 'c', label_md: '$x = 0$', is_correct: false },
          { id: 'd', label_md: 'Pas de solution', is_correct: false },
        ],
      },
      {
        id: '3e-ex-s5',
        prompt_md: 'Quelles sont les solutions de l’équation ?',
        explanation_md: 'Les deux solutions sont $x = 2$ et $x = -5$.',
        choices: [
          { id: 'a', label_md: 'Seulement $x = 2$', is_correct: false },
          { id: 'b', label_md: 'Seulement $x = -5$', is_correct: false },
          { id: 'c', label_md: '$x = 2$ et $x = -5$', is_correct: true },
          { id: 'd', label_md: '$x = 2$ et $x = 5$', is_correct: false },
        ],
      },
    ],
  },
});

const SECONDE: DemoContent = content('2nde', {
  warmup: [
    {
      id: '2nde-w1',
      prompt_md: 'Si $f(x) = 2x + 3$, alors $f(4) = ?$',
      explanation_md: 'On remplace $x$ par $4$ : $2 \\times 4 + 3 = 11$.',
      choices: [
        { id: 'a', label_md: '$8$', is_correct: false, feedback_md: 'Tu as oublié le $+3$.' },
        { id: 'b', label_md: '$11$', is_correct: true },
        { id: 'c', label_md: '$14$', is_correct: false },
        { id: 'd', label_md: '$24$', is_correct: false },
      ],
    },
    {
      id: '2nde-w2',
      prompt_md: 'Résous $2x - 6 = 0$.',
      explanation_md: '$2x = 6 \\iff x = 3$.',
      choices: [
        { id: 'a', label_md: '$x = -3$', is_correct: false },
        { id: 'b', label_md: '$x = 0$', is_correct: false },
        { id: 'c', label_md: '$x = 3$', is_correct: true },
        {
          id: 'd',
          label_md: '$x = 6$',
          is_correct: false,
          feedback_md: 'Tu n’as pas divisé par $2$.',
        },
      ],
    },
    {
      id: '2nde-w3',
      prompt_md: 'Forme canonique : $x^2 - 4x + 1 = ?$',
      explanation_md: '$(x - 2)^2 - 4 + 1 = (x - 2)^2 - 3$.',
      choices: [
        {
          id: 'a',
          label_md: '$(x - 2)^2 + 1$',
          is_correct: false,
          feedback_md: 'Oubli du $-4$ extrait du carré.',
        },
        { id: 'b', label_md: '$(x - 2)^2 - 3$', is_correct: true },
        { id: 'c', label_md: '$(x + 2)^2 - 3$', is_correct: false },
        { id: 'd', label_md: '$(x - 4)^2 + 1$', is_correct: false },
      ],
    },
    {
      id: '2nde-w4',
      prompt_md: 'Vecteur $\\vec{u}(2; -3)$. Quel est son sens ?',
      explanation_md:
        '$x = 2 > 0$ → va vers la droite. $y = -3 < 0$ → va vers le bas. Donc droite + bas.',
      choices: [
        { id: 'a', label_md: 'Droite + haut', is_correct: false },
        { id: 'b', label_md: 'Droite + bas', is_correct: true },
        { id: 'c', label_md: 'Gauche + haut', is_correct: false },
        { id: 'd', label_md: 'Gauche + bas', is_correct: false },
      ],
    },
    {
      id: '2nde-w5',
      prompt_md: 'Probabilité de tirer un trèfle dans un jeu de 32 cartes :',
      explanation_md: '$8$ trèfles sur $32$ cartes : $\\frac{8}{32} = \\frac{1}{4}$.',
      choices: [
        { id: 'a', label_md: '$\\frac{1}{2}$', is_correct: false },
        { id: 'b', label_md: '$\\frac{1}{4}$', is_correct: true },
        { id: 'c', label_md: '$\\frac{1}{8}$', is_correct: false },
        {
          id: 'd',
          label_md: '$\\frac{1}{32}$',
          is_correct: false,
          feedback_md: 'C’est la proba de tirer une carte précise.',
        },
      ],
    },
  ],
  exercise: {
    title: 'Étudier le signe d’une fonction affine',
    chapter_label: 'Fonctions',
    steps: [
      {
        id: '2nde-ex-s1',
        prompt_md: 'Soit $f(x) = -2x + 6$. Pour quelle valeur de $x$ a-t-on $f(x) = 0$ ?',
        explanation_md: '$-2x + 6 = 0 \\iff x = 3$.',
        choices: [
          { id: 'a', label_md: '$x = -3$', is_correct: false },
          { id: 'b', label_md: '$x = 3$', is_correct: true },
          { id: 'c', label_md: '$x = 6$', is_correct: false },
          { id: 'd', label_md: '$x = -6$', is_correct: false },
        ],
      },
      {
        id: '2nde-ex-s2',
        prompt_md: 'Quel est le coefficient directeur de $f$ ?',
        explanation_md: 'Pour $f(x) = ax + b$, le coefficient directeur est $a = -2$.',
        choices: [
          {
            id: 'a',
            label_md: '$6$',
            is_correct: false,
            feedback_md: 'C’est l’ordonnée à l’origine $b$.',
          },
          { id: 'b', label_md: '$-2$', is_correct: true },
          { id: 'c', label_md: '$2$', is_correct: false, feedback_md: 'Attention au signe.' },
          { id: 'd', label_md: '$3$', is_correct: false },
        ],
      },
      {
        id: '2nde-ex-s3',
        prompt_md: 'Le coefficient directeur est négatif. Cela signifie que $f$ est :',
        explanation_md:
          'Si $a < 0$, la fonction affine est strictement décroissante sur $\\mathbb{R}$.',
        choices: [
          { id: 'a', label_md: 'Constante', is_correct: false },
          { id: 'b', label_md: 'Strictement croissante', is_correct: false },
          { id: 'c', label_md: 'Strictement décroissante', is_correct: true },
          { id: 'd', label_md: 'Ni l’un ni l’autre', is_correct: false },
        ],
      },
      {
        id: '2nde-ex-s4',
        prompt_md: 'Sur quel intervalle a-t-on $f(x) > 0$ ?',
        explanation_md:
          'Décroissante, s’annule en $3$. Avant $3$, $f$ est positive ; après $3$, $f$ est négative.',
        choices: [
          {
            id: 'a',
            label_md: '$x > 3$',
            is_correct: false,
            feedback_md: 'C’est l’inverse — $f$ devient négative après.',
          },
          { id: 'b', label_md: '$x < 3$', is_correct: true },
          { id: 'c', label_md: 'Pour tout $x$', is_correct: false },
          { id: 'd', label_md: 'Aucun $x$', is_correct: false },
        ],
      },
    ],
  },
});

const PREMIERE: DemoContent = content('1re', {
  warmup: [
    {
      id: '1re-w1',
      prompt_md: 'Dérivée de $f(x) = x^3$ ?',
      explanation_md: '$(x^n)’ = n \\, x^{n-1}$, donc $f’(x) = 3x^2$.',
      choices: [
        { id: 'a', label_md: '$x^2$', is_correct: false },
        { id: 'b', label_md: '$3x$', is_correct: false, feedback_md: 'Attention à l’exposant.' },
        { id: 'c', label_md: '$3x^2$', is_correct: true },
        {
          id: 'd',
          label_md: '$x^4$',
          is_correct: false,
          feedback_md: 'C’est la primitive, pas la dérivée.',
        },
      ],
    },
    {
      id: '1re-w2',
      prompt_md: 'Dérivée de $\\sin x$ ?',
      explanation_md: '$(\\sin x)’ = \\cos x$.',
      choices: [
        { id: 'a', label_md: '$\\cos x$', is_correct: true },
        { id: 'b', label_md: '$-\\cos x$', is_correct: false },
        {
          id: 'c',
          label_md: '$-\\sin x$',
          is_correct: false,
          feedback_md: 'C’est la dérivée de $\\cos x$.',
        },
        { id: 'd', label_md: '$\\tan x$', is_correct: false },
      ],
    },
    {
      id: '1re-w3',
      prompt_md: 'Une suite arithmétique a $u_0 = 2$ et raison $r = 3$. Que vaut $u_4$ ?',
      explanation_md: '$u_n = u_0 + n r = 2 + 4 \\times 3 = 14$.',
      choices: [
        { id: 'a', label_md: '$11$', is_correct: false, feedback_md: 'Tu as compté $u_3$.' },
        { id: 'b', label_md: '$14$', is_correct: true },
        { id: 'c', label_md: '$17$', is_correct: false },
        {
          id: 'd',
          label_md: '$162$',
          is_correct: false,
          feedback_md: 'C’est une suite géométrique.',
        },
      ],
    },
    {
      id: '1re-w4',
      prompt_md: 'Si $f’(x) > 0$ sur un intervalle $I$, alors $f$ est :',
      explanation_md:
        'Une dérivée strictement positive sur un intervalle entraîne stricte croissance sur cet intervalle.',
      choices: [
        { id: 'a', label_md: 'Décroissante sur $I$', is_correct: false },
        { id: 'b', label_md: 'Constante sur $I$', is_correct: false },
        { id: 'c', label_md: 'Strictement croissante sur $I$', is_correct: true },
        { id: 'd', label_md: 'Indéterminée', is_correct: false },
      ],
    },
    {
      id: '1re-w5',
      prompt_md: 'Probabilité conditionnelle : $P_B(A) = ?$',
      explanation_md: 'Définition : $P_B(A) = \\frac{P(A \\cap B)}{P(B)}$ (si $P(B) > 0$).',
      choices: [
        { id: 'a', label_md: '$\\frac{P(A \\cap B)}{P(B)}$', is_correct: true },
        {
          id: 'b',
          label_md: '$P(A) \\times P(B)$',
          is_correct: false,
          feedback_md: 'C’est si $A$ et $B$ sont indépendants.',
        },
        { id: 'c', label_md: '$\\frac{P(B)}{P(A)}$', is_correct: false },
        { id: 'd', label_md: '$P(A) + P(B)$', is_correct: false },
      ],
    },
  ],
  exercise: {
    title: 'Étude des variations d’une fonction polynôme',
    chapter_label: 'Dérivation',
    steps: [
      {
        id: '1re-ex-s1',
        prompt_md: 'Soit $f(x) = x^2 - 4x + 3$. Quelle est sa dérivée ?',
        explanation_md: 'Dérivée terme à terme : $(x^2)’ = 2x$, $(-4x)’ = -4$, $(3)’ = 0$.',
        choices: [
          { id: 'a', label_md: '$f’(x) = 2x - 4$', is_correct: true },
          {
            id: 'b',
            label_md: '$f’(x) = 2x + 4$',
            is_correct: false,
            feedback_md: 'Attention au signe.',
          },
          { id: 'c', label_md: '$f’(x) = x - 4$', is_correct: false },
          { id: 'd', label_md: '$f’(x) = 2x - 4x + 3$', is_correct: false },
        ],
      },
      {
        id: '1re-ex-s2',
        prompt_md: 'Où s’annule $f’$ ?',
        explanation_md: '$2x - 4 = 0 \\iff x = 2$.',
        choices: [
          { id: 'a', label_md: '$x = 0$', is_correct: false },
          { id: 'b', label_md: '$x = 2$', is_correct: true },
          { id: 'c', label_md: '$x = -2$', is_correct: false },
          { id: 'd', label_md: '$x = 4$', is_correct: false },
        ],
      },
      {
        id: '1re-ex-s3',
        prompt_md: 'Quel est le signe de $f’$ pour $x > 2$ ?',
        explanation_md: 'Pour $x > 2$, $2x - 4 > 0$, donc $f’$ est positive.',
        choices: [
          { id: 'a', label_md: 'Positif', is_correct: true },
          {
            id: 'b',
            label_md: 'Négatif',
            is_correct: false,
            feedback_md: 'C’est le cas pour $x < 2$.',
          },
          { id: 'c', label_md: 'Nul', is_correct: false },
          { id: 'd', label_md: 'On ne peut pas savoir', is_correct: false },
        ],
      },
      {
        id: '1re-ex-s4',
        prompt_md: 'En $x = 2$, $f$ admet :',
        explanation_md:
          '$f’$ s’annule en changeant de signe (négative avant, positive après) : $f$ admet un minimum.',
        choices: [
          {
            id: 'a',
            label_md: 'Un maximum local',
            is_correct: false,
            feedback_md: 'Inversion du signe ici : minimum, pas maximum.',
          },
          { id: 'b', label_md: 'Un minimum local', is_correct: true },
          { id: 'c', label_md: 'Un point d’inflexion', is_correct: false },
          { id: 'd', label_md: 'Rien de particulier', is_correct: false },
        ],
      },
    ],
  },
});

const TERMINALE: DemoContent = content('tle', {
  warmup: [
    {
      id: 'tle-w1',
      prompt_md: '$\\lim_{x \\to +\\infty} \\dfrac{1}{x} = ?$',
      explanation_md: 'Quand $x \\to +\\infty$, $\\frac{1}{x} \\to 0^+$.',
      choices: [
        { id: 'a', label_md: '$+\\infty$', is_correct: false },
        { id: 'b', label_md: '$0$', is_correct: true },
        { id: 'c', label_md: '$1$', is_correct: false },
        { id: 'd', label_md: 'Indéterminée', is_correct: false },
      ],
    },
    {
      id: 'tle-w2',
      prompt_md: '$\\int_0^1 x \\, dx = ?$',
      explanation_md:
        'Primitive de $x$ : $\\frac{x^2}{2}$. Valeur : $\\frac{1}{2} - 0 = \\frac{1}{2}$.',
      choices: [
        { id: 'a', label_md: '$0$', is_correct: false },
        { id: 'b', label_md: '$\\frac{1}{2}$', is_correct: true },
        { id: 'c', label_md: '$1$', is_correct: false },
        { id: 'd', label_md: '$2$', is_correct: false },
      ],
    },
    {
      id: 'tle-w3',
      prompt_md: 'Dérivée de $e^x$ ?',
      explanation_md: '$(e^x)’ = e^x$ — c’est la propriété fondamentale de l’exponentielle.',
      choices: [
        {
          id: 'a',
          label_md: '$x e^{x-1}$',
          is_correct: false,
          feedback_md: 'C’est la dérivée de $x^n$.',
        },
        { id: 'b', label_md: '$e^{x+1}$', is_correct: false },
        { id: 'c', label_md: '$e^x$', is_correct: true },
        { id: 'd', label_md: '$x e^x$', is_correct: false },
      ],
    },
    {
      id: 'tle-w4',
      prompt_md: 'Loi normale centrée réduite : son espérance vaut :',
      explanation_md:
        '$\\mathcal{N}(0; 1)$ a une espérance $\\mu = 0$ et une variance $\\sigma^2 = 1$.',
      choices: [
        { id: 'a', label_md: '$0$', is_correct: true },
        { id: 'b', label_md: '$1$', is_correct: false, feedback_md: 'C’est la variance.' },
        { id: 'c', label_md: '$0{,}5$', is_correct: false },
        { id: 'd', label_md: 'On ne peut pas savoir', is_correct: false },
      ],
    },
    {
      id: 'tle-w5',
      prompt_md: 'Pour des vecteurs $\\vec{u}$ et $\\vec{v}$ orthogonaux, on a :',
      explanation_md: '$\\vec{u} \\perp \\vec{v} \\iff \\vec{u} \\cdot \\vec{v} = 0$.',
      choices: [
        { id: 'a', label_md: '$\\vec{u} \\cdot \\vec{v} = 1$', is_correct: false },
        { id: 'b', label_md: '$\\vec{u} \\cdot \\vec{v} = 0$', is_correct: true },
        {
          id: 'c',
          label_md: '$||\\vec{u}|| = ||\\vec{v}||$',
          is_correct: false,
          feedback_md: 'Ça, c’est si les normes sont égales.',
        },
        { id: 'd', label_md: '$\\vec{u} + \\vec{v} = \\vec{0}$', is_correct: false },
      ],
    },
  ],
  exercise: {
    title: 'Calcul d’une intégrale simple',
    chapter_label: 'Intégration',
    steps: [
      {
        id: 'tle-ex-s1',
        prompt_md:
          'On veut calculer $I = \\int_0^2 (2x + 1) \\, dx$. Quelle est la primitive de $2x + 1$ ?',
        explanation_md: 'Primitive de $2x$ : $x^2$. Primitive de $1$ : $x$. Total : $x^2 + x$.',
        choices: [
          { id: 'a', label_md: '$x^2 + x$', is_correct: true },
          {
            id: 'b',
            label_md: '$x^2 + 1$',
            is_correct: false,
            feedback_md: 'La primitive de $1$ est $x$, pas $1$.',
          },
          { id: 'c', label_md: '$2x^2 + x$', is_correct: false },
          {
            id: 'd',
            label_md: '$x + 1$',
            is_correct: false,
            feedback_md: 'C’est la dérivée, pas la primitive.',
          },
        ],
      },
      {
        id: 'tle-ex-s2',
        prompt_md: 'Quelle formule applique-t-on pour calculer $I$ ?',
        explanation_md: '$I = [F(x)]_0^2 = F(2) - F(0)$, où $F$ est une primitive.',
        choices: [
          { id: 'a', label_md: '$F(2) \\times F(0)$', is_correct: false },
          { id: 'b', label_md: '$F(2) + F(0)$', is_correct: false },
          { id: 'c', label_md: '$F(2) - F(0)$', is_correct: true },
          { id: 'd', label_md: '$\\frac{F(2)}{F(0)}$', is_correct: false },
        ],
      },
      {
        id: 'tle-ex-s3',
        prompt_md: 'Que vaut $F(2) = 2^2 + 2$ ?',
        explanation_md: '$4 + 2 = 6$.',
        choices: [
          { id: 'a', label_md: '$4$', is_correct: false },
          { id: 'b', label_md: '$6$', is_correct: true },
          { id: 'c', label_md: '$8$', is_correct: false },
          { id: 'd', label_md: '$10$', is_correct: false },
        ],
      },
      {
        id: 'tle-ex-s4',
        prompt_md: 'Combien vaut $I$ ?',
        explanation_md: '$F(0) = 0$, donc $I = 6 - 0 = 6$.',
        choices: [
          { id: 'a', label_md: '$0$', is_correct: false },
          { id: 'b', label_md: '$4$', is_correct: false },
          { id: 'c', label_md: '$6$', is_correct: true },
          {
            id: 'd',
            label_md: '$12$',
            is_correct: false,
            feedback_md: 'Tu as additionné $F(2) + F(0)$.',
          },
        ],
      },
    ],
  },
});

export const DEMO_FALLBACK_CONTENT: Record<SchoolLevel, DemoContent> = {
  '6e': SIXIEME,
  '5e': CINQUIEME,
  '4e': QUATRIEME,
  '3e': TROISIEME,
  '2nde': SECONDE,
  '1re': PREMIERE,
  tle: TERMINALE,
  // Prépa : pas de contenu de démo dédié, repli sur la Terminale.
  mpsi: TERMINALE,
  ptsi: TERMINALE,
  mp: TERMINALE,
  pt: TERMINALE,
};

export function getFallbackContent(level: SchoolLevel): DemoContent {
  return DEMO_FALLBACK_CONTENT[level];
}
